import { useSearchParams } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { ISSUE_STATUS } from "contracts/enums/core";
import { createMemo, For } from "solid-js";
import {
  EffortBadge,
  LifecycleBadge,
  SeverityBadge,
  StatusBadge,
} from "../../ui/Badge";
import { relativeAge } from "../../ui/format";
import { uniqueCurrentPagesForIssue } from "../scan/issuePageResolution";
import { clientIssueTitle } from "../scan/issueText";
import { metricLabel } from "../website/issueModel";

const COLUMNS = [
  "",
  "Issue",
  "Severity",
  "Status",
  "Lifecycle",
  "Effort",
  "Pages",
  "Updated",
];

export function IssuesTable(props: {
  data: OverviewResponse;
  issues: Issue[];
  selectedId: string | null;
}) {
  return (
    <table class="tbl">
      <thead>
        <tr>
          <For each={COLUMNS}>{(label) => <th>{label}</th>}</For>
        </tr>
      </thead>
      <tbody>
        <For each={props.issues}>
          {(issue) => (
            <IssueRow
              data={props.data}
              issue={issue}
              selected={issue.id === props.selectedId}
            />
          )}
        </For>
      </tbody>
    </table>
  );
}

function IssueRow(props: {
  data: OverviewResponse;
  issue: Issue;
  selected: boolean;
}) {
  const [, setSearch] = useSearchParams();
  const done = () =>
    props.issue.status === ISSUE_STATUS.DONE ||
    props.issue.status === ISSUE_STATUS.IGNORED;
  const open = () => setSearch({ issue: props.issue.id });
  const title = () => clientIssueTitle(props.issue);
  const currentPages = createMemo(() =>
    uniqueCurrentPagesForIssue(props.data, props.issue),
  );

  return (
    <tr
      class={`tbl-row ${props.selected ? "is-selected" : ""} ${done() ? "is-muted" : ""}`}
    >
      <td class="td-sev-rail">
        <span
          class={`sev-rail sev-${props.issue.severity}`}
          aria-hidden="true"
        />
      </td>
      <td class="td-issue">
        <button
          class="issue-row-open"
          type="button"
          aria-label={`Open quick look for ${title()}`}
          onClick={open}
        >
          <span class="td-issue-title">{title()}</span>
          <span class="td-issue-sub">
            <span class="mono td-page">
              {currentPageSummary(currentPages())}
            </span>
            <span class="td-metric">{metricLabel(props.issue.metricKey)}</span>
          </span>
          <span class="issue-row-action">Quick look</span>
        </button>
      </td>
      <td class="issue-row-priority" data-label="Severity">
        <SeverityBadge severity={props.issue.severity} />
      </td>
      <td class="issue-row-status" data-label="Status">
        <StatusBadge status={props.issue.status} />
      </td>
      <td class="issue-row-secondary" data-label="Lifecycle">
        <LifecycleBadge state={props.issue.lifecycleState} />
      </td>
      <td class="issue-row-secondary" data-label="Effort">
        <EffortBadge effort={props.issue.effort} />
      </td>
      <td class="td-num issue-row-pages" data-label="Affected pages">
        <span class="mono">{currentPages().length}</span>
      </td>
      <td class="td-num mono td-age issue-row-secondary" data-label="Updated">
        {relativeAge(asDate(props.issue.updatedAt))}
      </td>
    </tr>
  );
}

function currentPageSummary(pages: OverviewResponse["pages"]): string {
  const first = pages[0];
  if (!first) return "No current page";
  return pages.length === 1 ? first.path : `${first.path} +${pages.length - 1}`;
}

function asDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}
